/**
 * Google Analytics (GA4).
 *
 * Tracking is limited to two hosts: `cornerstone.libertyrent.com` (production id)
 * and `cornerstage.libertyrent.com` (stage id). Anywhere else — local, dev,
 * preprod, the `v2-*` hosts — every helper here is a no-op, and `index.html`
 * skips loading gtag.js entirely. Keep both host lists in sync.
 *
 * User-action events go through `trackApplicationEvent`, which attaches
 * `user_role` from the authenticated session and skips the event when the role
 * cannot be mapped or the session is impersonating. Technical events
 * (`screen_view`, `page_load_timing`, `exception`) do not include a role.
 */


import type { AnalyticsParams, ApplicationSection } from './analytics.types'

export type { AnalyticsParams, ApplicationSection } from './analytics.types'

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void
    dataLayer?: unknown[]
  }
}

export const GA_ID_STAGE = 'G-SSK09B6PVC'
export const GA_ID_PROD = 'G-0GW29BP41X'

export const GA_PROD_HOST = 'cornerstone.libertyrent.com'
export const GA_STAGE_HOST = 'cornerstage.libertyrent.com'

/** Test seam. Vite inlines `import.meta.env.DEV`, so tests replace this method. */
export const analyticsEnv = {
  isDevelopment(): boolean {
    return import.meta.env.DEV
  },
}

function canTrack(): boolean {
  return (
    !analyticsEnv.isDevelopment() &&
    typeof window !== 'undefined' &&
    getAnalyticsId() !== '' &&
    typeof window.gtag === 'function'
  )
}

/** GA4 measurement id for the current host, or `''` on untracked hosts. */
export function getAnalyticsId(): string {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : ''
  if (hostname === GA_PROD_HOST) return import.meta.env.VITE_GA_ID_PROD || GA_ID_PROD
  if (hostname === GA_STAGE_HOST) return import.meta.env.VITE_GA_ID_STAGE || GA_ID_STAGE
  return ''
}

export const GA_USER_ROLE_BY_SESSION_ROLE: Record<string, string> = {
  ROLE_ADMIN: 'liberty_admin',
  ROLE_EXECS: 'liberty_execs',
  ROLE_LIBERTY_DEFAULTS: 'liberty_default',
  ROLE_UNDERWRITER: 'liberty_underwriter',
  ROLE_LIBERTY_SALES: 'liberty_sales',
  ROLE_CUSTOMER_SERVICE: 'liberty_customer_service',
  ROLE_CUSTOMER_SERVICE_MANAGER: 'liberty_customer_service_manager',
  ROLE_LIBERTY_FIN_MGR: 'liberty_finance_manager',
  ROLE_LIBERTY_ACCOUNTING: 'liberty_accounting',
  EXTERNAL_INSURANCE: 'external_insurance',
  ROLE_REFUND_APPROVAL: 'liberty_refund_approval',
  ROLE_COMPANY_ADMIN: 'company_admin',
  ROLE_COMPANY_MANAGER: 'company_manager',
  ROLE_REGIONAL_MANAGER: 'company_regional_manager',
  ROLE_PROPERTY_ADMIN: 'property_assistant',
  ROLE_PROPERTY_MANAGER: 'property_manager',
  ROLE_PROPERTY_AGENT: 'leasing_consultant',
  ROLE_RENTER: 'resident',
}

const BLOCKED_PARAM_KEYS = new Set([
  'applicant_id',
  'applicantId',
  'user_id',
  'userId',
  'email',
  'event_label',
  'card_number',
  'cvv',
  'auth_code',
  'account_number',
])

function readStoredUser(): { user_id?: string | number; current_role?: { role_name?: string } } | undefined {
  try {
    const raw = localStorage.getItem('user')
    if (!raw) return undefined
    return JSON.parse(raw) as { user_id?: string | number; current_role?: { role_name?: string } }
  } catch {
    return undefined
  }
}

/**
 * Actor role for GA4. Returns undefined when impersonating or when the session
 * role is missing or unmapped. Callers must not substitute `resident`.
 */
export function resolveAnalyticsUserRole(): string | undefined {
  const roleName = readStoredUser()?.current_role?.role_name
  if (!roleName) return undefined
  return GA_USER_ROLE_BY_SESSION_ROLE[roleName]
}

function readStoredUserId(): string | undefined {
  const userId = readStoredUser()?.user_id
  return userId != null ? String(userId) : undefined
}

export function setAnalyticsUser(): void {
  if (!canTrack()) return
  window.gtag!('set', { cookie_flags: 'SameSite=None;Secure' })
  const userId = readStoredUserId()
  window.gtag!('config', getAnalyticsId(), userId ? { user_id: userId } : {})
}

export function setScreenView(screenName: string): void {
  if (!canTrack()) return
  const appName = import.meta.env.VITE_APP_NAME || 'Liberty Rent'
  window.gtag!('event', 'screen_view', {
    app_name: appName,
    screen_name: screenName,
    event_category: 'navigation',
  })
}

function cleanParams(parameters?: AnalyticsParams): Record<string, string | number | boolean> {
  const cleaned: Record<string, string | number | boolean> = {}
  if (!parameters) return cleaned
  for (const [key, value] of Object.entries(parameters)) {
    if (BLOCKED_PARAM_KEYS.has(key)) continue
    if (value === undefined || value === null || value === '') continue
    cleaned[key] = value
  }
  return cleaned
}

/**
 * Shared path for named user-action events. Skips the hit when tracking is off,
 * the session is impersonating, or `user_role` cannot be mapped.
 */
export function trackApplicationEvent(
  eventName: string,
  category: string,
  parameters?: AnalyticsParams,
): void {
  if (!canTrack()) return
  const userRole = resolveAnalyticsUserRole()
  if (!userRole) return
  window.gtag!('event', eventName, {
    event_category: category,
    user_role: userRole,
    ...cleanParams(parameters),
  })
}

export function majorSectionForTab(
  tab: string,
): { section: ApplicationSection; sectionNumber: number } | null {
  switch (tab) {
    case 'about':
    case 'address':
      return { section: 'about_yourself', sectionNumber: 1 }
    case 'property':
      return { section: 'property', sectionNumber: 2 }
    case 'income':
      return { section: 'income_and_employment', sectionNumber: 3 }
    case 'history':
      return { section: 'personal_history_situation', sectionNumber: 4 }
    case 'signature':
      return { section: 'acknowledgements_identity', sectionNumber: 5 }
    case 'payment':
      return { section: 'payment', sectionNumber: 6 }
    default:
      return null
  }
}

export function trackApplicationSectionView(section: ApplicationSection, sectionNumber: number): void {
  trackApplicationEvent('application_section_view', 'resident_application', {
    application_section: section,
    application_section_number: sectionNumber,
  })
}

export function trackApplicationSectionComplete(section: ApplicationSection, sectionNumber: number): void {
  trackApplicationEvent('application_section_complete', 'resident_application', {
    application_section: section,
    application_section_number: sectionNumber,
  })
}

export function trackApplicationAutosave(
  section: ApplicationSection,
  sectionNumber: number,
  actionResult: 'success' | 'error',
): void {
  trackApplicationEvent('application_autosave', 'resident_application', {
    application_section: section,
    application_section_number: sectionNumber,
    action_result: actionResult,
  })
}

export function trackApplicationExit(section: ApplicationSection, sectionNumber: number): void {
  trackApplicationEvent('application_exit', 'resident_application', {
    application_section: section,
    application_section_number: sectionNumber,
  })
}

export function trackApplicationAction(
  section: ApplicationSection,
  sectionNumber: number,
  actionName: string,
  additionalParameters?: AnalyticsParams,
): void {
  trackApplicationEvent('application_action', 'resident_application', {
    application_section: section,
    application_section_number: sectionNumber,
    action_name: actionName,
    ...additionalParameters,
  })
}

export function trackAccessFeePaid(): void {
  trackApplicationEvent('access_fee_paid', 'payment', {
    application_section: 'payment',
    application_section_number: 6,
  })
}

export function trackApplicationFeePaid(): void {
  trackApplicationEvent('application_fee_paid', 'payment', {
    application_section: 'payment',
    application_section_number: 6,
  })
}

export function trackDefaultInvoicePaid(): void {
  trackApplicationEvent('default_invoice_paid', 'payment')
}

export function sendPageLoadingTiming(value?: number): void {
  if (!canTrack()) return
  window.gtag!('event', 'page_load_timing', {
    event_category: 'navigation',
    timing_name: 'load',
    value,
  })
}

/** Error name only. Do not pass a URL, message, or stack. */
export function sendException(errorName: string): void {
  if (!canTrack()) return
  const description = errorName.trim().split(/[\s:]/)[0]?.slice(0, 80) || 'Error'
  window.gtag!('event', 'exception', {
    event_category: 'navigation',
    description,
    fatal: false,
  })
}

/** Stable route name with numeric id segments removed. */
export function screenNameFromPath(pathname: string): string {
  const segments = pathname
    .split('/')
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0 && !/^\d+$/.test(segment))
  return segments.join('-')
}
